import React, { useMemo, useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { fmtDate, fmtTime, type ApprovalInboxItem, type ID } from '@hrms/shared';
import { api } from '../../../lib/api';
import { humanize } from '../../../lib/date';
import { useBreakpoint } from '../../../lib/hooks';
import { useMut } from '../../../lib/query';
import { useKeyShortcuts } from '../../hr/components/keys';
import { fmtVal } from '../../hr/components/misc';
import {
  ApprovalTimeline, Badge, Button, Card, Checkbox, DataTable, DescList, EmptyState, Loading, Modal, PageHeader, Row, Screen, Select, Stack, StatusBadge, Tabs, Text, TextField, useTheme, useToast,
} from '../../../ui';

const KINDS = [
  { value: '', label: 'All types' },
  { value: 'leave_request', label: 'Leave' },
  { value: 'regularisation', label: 'Regularisation' },
  { value: 'profile_change', label: 'Profile change' },
  { value: 'comp_off', label: 'Comp-off' },
];
const HIDDEN_SUBJECT_KEYS = ['id', 'employee_id', 'workflow_instance_id', 'employee', 'workflow'];

/** Approvals inbox: pending / done, filter by type, approve / reject with comment, timeline, bulk approve. */
export function ApprovalsScreen() {
  const { colors } = useTheme();
  const { isTablet } = useBreakpoint();
  const toast = useToast();
  const [status, setStatus] = useState<'pending' | 'done'>('pending');
  const [kind, setKind] = useState('');
  const [page, setPage] = useState(1);
  const [sel, setSel] = useState<ApprovalInboxItem | null>(null);
  const [comment, setComment] = useState('');
  const [commentErr, setCommentErr] = useState('');
  const [checked, setChecked] = useState<Set<ID>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const q = useQuery({ queryKey: ['approvals', status, kind, page], queryFn: () => api.workflow.inbox({ status, key: kind || undefined, page }) });
  const inst = useQuery({ queryKey: ['workflow-instance', sel?.instance.id], queryFn: () => api.workflow.instance(sel!.instance.id), enabled: !!sel });

  const after = {
    invalidate: [['approvals'], ['leave'], ['regularisations'], ['dashboard'], ['workflow-instance']],
    onSuccess: () => {
      setSel(null);
      setComment('');
    },
  };
  const approve = useMut((v: { id: ID; comment?: string }) => api.workflow.approve(v.id, v.comment), { ...after, success: 'Approved' });
  const reject = useMut((v: { id: ID; comment: string }) => api.workflow.reject(v.id, v.comment), { ...after, success: 'Rejected' });

  const items = useMemo(() => q.data?.data ?? [], [q.data]);
  const pendingItems = useMemo(() => items.filter((i) => i.status === 'pending'), [items]);
  const allChecked = pendingItems.length > 0 && pendingItems.every((i) => checked.has(i.id));

  const open = (a: ApprovalInboxItem) => {
    setSel(a);
    setComment('');
    setCommentErr('');
  };
  const doApprove = () => sel && approve.mutate({ id: sel.id, comment: comment.trim() || undefined });
  const doReject = () => {
    if (!sel) return;
    if (!comment.trim()) return setCommentErr('A comment is required to reject');
    reject.mutate({ id: sel.id, comment: comment.trim() });
  };
  useKeyShortcuts(!!sel && sel.status === 'pending', (key, e) => {
    if (e.ctrl && key === 'enter') doApprove();
    else if (!e.typing && key === 'a') doApprove();
    else if (!e.typing && key === 'r') doReject();
  });

  const toggle = (id: ID, v: boolean) => {
    const n = new Set(checked);
    if (v) n.add(id);
    else n.delete(id);
    setChecked(n);
  };
  const bulkApprove = async () => {
    setBulkBusy(true);
    let ok = 0;
    const ids = [...checked];
    for (const id of ids) {
      try {
        await api.workflow.approve(id);
        ok++;
      } catch {
        /* keep going; report the count */
      }
    }
    setBulkBusy(false);
    setChecked(new Set());
    if (ok === ids.length) toast.success(`Approved ${ok} request${ok === 1 ? '' : 's'}`);
    else toast.error(`Approved ${ok} of ${ids.length}; the rest failed`);
    void q.refetch();
  };

  const changeStatus = (s: 'pending' | 'done') => {
    setStatus(s);
    setPage(1);
    setChecked(new Set());
  };

  const subject = sel ? Object.entries(sel.subject ?? {}).filter(([k]) => !HIDDEN_SUBJECT_KEYS.includes(k)) : [];

  return (
    <Screen refreshing={q.isRefetching} onRefresh={() => q.refetch()} testID="approvals">
      <PageHeader title="Approvals" subtitle="Leave, regularisation, profile change and comp-off requests awaiting you." />
      <Tabs
        value={status}
        onChange={changeStatus}
        tabs={[
          { key: 'pending', label: 'Pending', count: status === 'pending' ? q.data?.total : undefined },
          { key: 'done', label: 'Done' },
        ]}
      />
      <Row gap={8}>
        <Select containerStyle={{ minWidth: 200 }} value={kind} onChange={(k) => { setKind(k); setPage(1); setChecked(new Set()); }} options={KINDS} testID="approvals-kind" />
        {status === 'pending' && pendingItems.length > 0 && <Checkbox label="Select all" value={allChecked} onChange={(v) => setChecked(v ? new Set(pendingItems.map((i) => i.id)) : new Set())} />}
        {checked.size > 0 && <Button variant="success" icon="check" loading={bulkBusy} title={`Approve ${checked.size} selected`} onPress={bulkApprove} testID="approvals-bulk" />}
      </Row>

      {isTablet ? (
        <DataTable
          rows={q.data}
          loading={q.isLoading}
          onPageChange={setPage}
          onRowPress={open}
          emptyTitle={status === 'pending' ? 'Inbox zero — nothing pending' : 'No decisions yet'}
          columns={[
            ...(status === 'pending'
              ? [{ key: 'sel', title: '', width: 44, flex: 0, sortable: false, render: (r: ApprovalInboxItem) => <Checkbox label="" value={checked.has(r.id)} onChange={(v) => toggle(r.id, v)} /> }]
              : []),
            { key: 'type', title: 'Type', width: 140, render: (r) => <Badge tone="info">{r.instance.definition.name}</Badge>, sortValue: (r) => r.instance.definition.key },
            { key: 'requester', title: 'Requested by', width: 150, render: (r) => r.instance.requester?.name ?? '—', sortValue: (r) => r.instance.requester?.name },
            { key: 'summary', title: 'Summary', width: 240, flex: 3 },
            { key: 'name', title: 'Step', width: 130 },
            { key: 'created', title: 'Submitted', width: 110, render: (r) => fmtDate(r.instance.created_at), sortValue: (r) => r.instance.created_at },
            { key: 'due_at', title: 'Due', width: 100, render: (r) => (r.due_at ? fmtDate(r.due_at) : '—'), sortValue: (r) => r.due_at },
            { key: 'status', title: 'Status', width: 100, render: (r) => <StatusBadge status={r.status} />, sortValue: (r) => r.status },
          ]}
        />
      ) : q.isLoading ? (
        <Loading />
      ) : !items.length ? (
        <Card>
          <EmptyState icon="inbox" title={status === 'pending' ? 'Inbox zero — nothing pending' : 'No decisions yet'} />
        </Card>
      ) : (
        <Stack gap={10}>
          {items.map((a) => (
            <Card key={a.id} onPress={() => open(a)} testID={`approval-${a.id}`}>
              <Row align="flex-start" wrap={false} gap={10}>
                {a.status === 'pending' && <Checkbox label="" value={checked.has(a.id)} onChange={(v) => toggle(a.id, v)} />}
                <View style={{ flex: 1, gap: 4 }}>
                  <Row gap={6}>
                    <Badge tone="info">{a.instance.definition.name}</Badge>
                    <StatusBadge status={a.status} />
                  </Row>
                  <Text weight="semibold">{a.summary}</Text>
                  <Text variant="small">
                    {a.instance.requester?.name ?? '—'} · {fmtDate(a.instance.created_at)}
                    {a.due_at ? ` · due ${fmtDate(a.due_at)}` : ''}
                  </Text>
                </View>
              </Row>
            </Card>
          ))}
          {q.data && q.data.last_page > 1 && (
            <Row justify="space-between">
              <Text variant="small">
                Page {q.data.current_page} of {q.data.last_page}
              </Text>
              <Row gap={6}>
                <Button size="sm" icon="chevronLeft" title="Prev" disabled={q.data.current_page <= 1} onPress={() => setPage(q.data!.current_page - 1)} />
                <Button size="sm" iconRight="chevronRight" title="Next" disabled={q.data.current_page >= q.data.last_page} onPress={() => setPage(q.data!.current_page + 1)} />
              </Row>
            </Row>
          )}
        </Stack>
      )}

      <Modal
        open={!!sel}
        onClose={() => setSel(null)}
        title={sel?.instance.definition.name ?? ''}
        subtitle={sel ? `${sel.instance.requester?.name ?? ''} · ${fmtDate(sel.instance.created_at)} ${fmtTime(sel.instance.created_at)}` : undefined}
        width={560}
        testID="approval-sheet"
        footer={
          sel?.status === 'pending' ? (
            <>
              <Button variant="danger" icon="x" title="Reject" loading={reject.isPending} onPress={doReject} testID="approval-reject" />
              <Button variant="success" icon="check" title="Approve" loading={approve.isPending} onPress={doApprove} testID="approval-approve" />
            </>
          ) : undefined
        }
      >
        {sel && (
          <>
            <View style={{ padding: 12, borderRadius: 8, backgroundColor: colors.surface2, borderWidth: 1, borderColor: colors.border }}>
              <Text weight="semibold">{sel.summary}</Text>
              <Text variant="small">Step: {sel.name}</Text>
            </View>
            {subject.length > 0 && <DescList items={subject.map(([k, v]) => [humanize(k), fmtVal(v)])} />}
            {sel.status === 'pending' && (
              <TextField
                label="Comment"
                multiline
                value={comment}
                onChangeText={(c) => {
                  setComment(c);
                  if (c.trim()) setCommentErr('');
                }}
                error={commentErr}
                hint="Required when rejecting. Shortcuts on web: A approve · R reject · Ctrl+Enter approve"
                testID="approval-comment"
              />
            )}
            <Text variant="title">Timeline</Text>
            {inst.isLoading ? <Loading /> : <ApprovalTimeline instance={inst.data ?? sel.instance} />}
          </>
        )}
      </Modal>
    </Screen>
  );
}
